import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, X, GripVertical } from 'lucide-react';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import api from '../../services/api';

const CATEGORIES = ['Medical', 'Daily Care', 'Maintenance', 'Conditioning'];
const CARE_TYPES = {
  'Medical':      ['Vaccination', 'Medication', 'Supplement', 'Procedure', 'Exam', 'Foaling', 'Other'],
  'Daily Care':   ['Feed', 'Water', 'Groom', 'Wraps & Packs', 'Order Supplies / Tack', 'Boarding'],
  'Maintenance':  ['Farrier', 'Dental', 'Chiropractic', 'Body Work / Massage', 'Other'],
  'Conditioning': ['Exercise', 'Training', 'Rehab', 'Other'],
};

const uid = () => Math.random().toString(36).slice(2);

const EMPTY_ITEM = () => ({
  id: uid(),
  day_offset: 0,
  trigger: 'start_date',
  category: 'Medical',
  type: 'Vaccination',
  name: '',
  care_catalog_item_id: null,
  dosage: '',
  dosage_unit: '',
  units: '',
  preferred_time: '',
  notes: '',
});

export default function ProtocolModal({ open, onClose, onSaved, editing, catalogItems, locations }) {
  const [name, setName]           = useState('');
  const [reason, setReason]       = useState('');
  const [isBreeding, setIsBreeding] = useState(false);
  const [items, setItems]         = useState([EMPTY_ITEM()]);
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name || '');
      setReason(editing.reason || '');
      setIsBreeding(!!editing.is_breeding);
      setItems((editing.schedule_items || []).map(i => ({ ...i, id: i.id || uid() })));
    } else {
      setName('');
      setReason('');
      setIsBreeding(false);
      setItems([EMPTY_ITEM()]);
    }
  }, [open, editing]);

  const addItem    = ()          => setItems(prev => [...prev, EMPTY_ITEM()]);
  const removeItem = (id)        => setItems(prev => prev.filter(i => i.id !== id));
  const updateItem = (id, patch) => setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));

  const handleCatalogSelect = (itemId, schedItemId) => {
    const cat = catalogItems?.find(c => c.id === itemId);
    if (cat) {
      updateItem(schedItemId, {
        care_catalog_item_id: cat.id,
        category:    cat.category,
        type:        cat.type,
        name:        cat.name,
        dosage:      cat.dosage || '',
        dosage_unit: cat.dosage_unit || '',
        units:       cat.units ?? '',
        billable:    cat.billable || false,
        cost:        cat.cost ?? '',
        cost_currency: cat.cost_currency || 'USD',
      });
    }
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Protocol name is required'); return; }
    if (items.some(i => !i.name.trim())) { toast.error('All schedule items need a name'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        reason,
        is_breeding: isBreeding,
        schedule_items: items.map(({ id, ...rest }) => rest),
      };
      if (editing) {
        await api.put(`/farmfolio/protocols/${editing.id}`, payload);
        toast.success('Protocol updated');
      } else {
        await api.post('/farmfolio/protocols', payload);
        toast.success('Protocol created');
      }
      onSaved();
      onClose();
    } catch { toast.error('Failed to save protocol'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Protocol' : 'Create Protocol'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="font-semibold">Protocol Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Newborn Foal Protocol" />
            </div>
            <div className="space-y-1">
              <Label className="font-semibold">Reason</Label>
              <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Standard foal care" />
            </div>
          </div>

          {/* Breeding toggle */}
          <div className="flex items-center gap-3">
            <Switch checked={isBreeding} onCheckedChange={setIsBreeding} id="proto-breeding" />
            <label htmlFor="proto-breeding" className="text-sm cursor-pointer">
              Breeding Protocol
              <span className="block text-xs text-muted-foreground">Only appears in the Breeding Season care section</span>
            </label>
          </div>

          {/* Protocol Schedule */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="font-semibold">Protocol Schedule</Label>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={addItem}>
                <Plus className="w-3 h-3 mr-1" /> Add Item
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
            </p>

            <div className="border rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/10 border-b">
                  <tr>
                    <th className="text-left p-2 font-medium w-16">Days</th>
                    <th className="text-left p-2 font-medium w-32">After</th>
                    <th className="text-left p-2 font-medium">Category</th>
                    <th className="text-left p-2 font-medium">Type</th>
                    <th className="text-left p-2 font-medium">Name</th>
                    <th className="text-left p-2 font-medium w-20">Time</th>
                    <th className="p-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(item => {
                    const catCatalog = (catalogItems || []).filter(c => c.category === item.category && c.type === item.type);
                    return (
                      <tr key={item.id} className="border-b last:border-0">
                        <td className="p-1.5">
                          <Input type="number" min="0" step="1" value={item.day_offset}
                            onChange={e => updateItem(item.id, { day_offset: parseInt(e.target.value) || 0 })}
                            className="h-7 text-xs w-16" />
                        </td>
                        <td className="p-1.5">
                          <Select value={item.trigger || 'start_date'} onValueChange={v => updateItem(item.id, { trigger: v })}>
                            <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="start_date">Start Date</SelectItem>
                              <SelectItem value="from_previous">Prev Item</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-1.5">
                          <Select value={item.category} onValueChange={v => updateItem(item.id, { category: v, type: CARE_TYPES[v][0], name: '', care_catalog_item_id: null })}>
                            <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="p-1.5">
                          <Select value={item.type} onValueChange={v => updateItem(item.id, { type: v, name: '', care_catalog_item_id: null })}>
                            <SelectTrigger className="h-7 text-xs w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>{(CARE_TYPES[item.category] || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                          </Select>
                        </td>
                        <td className="p-1.5">
                          {catCatalog.length > 0 ? (
                            <Select value={item.care_catalog_item_id || '__none__'}
                              onValueChange={v => v === '__none__' ? updateItem(item.id, { name: '', care_catalog_item_id: null }) : handleCatalogSelect(v, item.id)}>
                              <SelectTrigger className="h-7 text-xs min-w-[120px]">
                                <SelectValue placeholder="Select…" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">— Select —</SelectItem>
                                {catCatalog.map(c => {
                                  const dosageDetail = c.dosage ? `${c.dosage}${c.dosage_unit ? ' ' + c.dosage_unit : ''}` : '';
                                  return <SelectItem key={c.id} value={c.id}>{c.name}{dosageDetail ? ` (${dosageDetail})` : ''}{(c.is_billable ?? c.billable) ? ' (billable)' : ''}</SelectItem>;
                                })}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input value={item.name} onChange={e => updateItem(item.id, { name: e.target.value })}
                              placeholder="Name…" className="h-7 text-xs min-w-[120px]" />
                          )}
                        </td>
                        <td className="p-1.5">
                          <input type="time" value={item.preferred_time || ''}
                            onChange={e => updateItem(item.id, { preferred_time: e.target.value })}
                            className={`h-7 text-xs border rounded px-1.5 w-20 focus:outline-none focus:ring-1 focus:ring-primary ${item.preferred_time ? 'time-filled' : ''}`} />
                        </td>
                        <td className="p-1.5">
                          <button onClick={() => removeItem(item.id)}
                            className="text-muted-foreground hover:text-destructive transition-colors">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Notes (optional)</Label>
            <Textarea rows={2} value={reason}
              onChange={e => setReason(e.target.value)} placeholder="Additional details about this protocol" />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}
            className="bg-primary hover:bg-primary/90 text-white">
            {saving ? 'Saving…' : editing ? 'Update Protocol' : 'Create Protocol'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
